import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  List,
  ListItem,
  Avatar,
  Chip,
  IconButton,
  Collapse,
  useTheme,
  alpha,
  CircularProgress
} from '@mui/material';
import {
  Search as SearchIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Assignment as TaskIcon,
  Image as ImageIcon
} from '@mui/icons-material';
// Drag and drop implemented with native HTML5 API
import useTaskStore from '../../../stores/taskStore';
import { EntityStatusBadge } from '../../core';

// Draggable Task Item
const DraggableTask = ({ task }) => {
  const theme = useTheme();
  const [isDragging, setIsDragging] = useState(false);

  const handleDragStart = (e) => {
    setIsDragging(true);
    e.dataTransfer.setData('task', JSON.stringify(task));
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  const hasImage = task.attachments?.some(att => att.mimetype?.startsWith('image/'));

  return (
    <ListItem
      draggable
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      sx={{
        mb: 1,
        borderRadius: 1,
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: theme.palette.background.paper,
        cursor: 'grab',
        opacity: isDragging ? 0.5 : 1,
        transition: 'all 0.2s ease',
        '&:hover': {
          bgcolor: alpha(theme.palette.primary.main, 0.05),
          transform: 'translateX(4px)'
        }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1 }}>
        {/* Task Icon or Image Preview */}
        {hasImage ? (
          <Avatar
            variant="rounded"
            sx={{ 
              width: 40, 
              height: 40,
              bgcolor: alpha(theme.palette.primary.main, 0.1)
            }}
          >
            <ImageIcon color="primary" />
          </Avatar>
        ) : (
          <Avatar
            variant="rounded"
            sx={{ 
              width: 40, 
              height: 40,
              bgcolor: alpha(theme.palette.primary.main, 0.1)
            }}
          >
            <TaskIcon color="primary" />
          </Avatar>
        )}

        {/* Task Info */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 'medium',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {task.title}
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
            <EntityStatusBadge 
              status={task.status} 
              entityType="task" 
              size="small" 
            />
            {task.priority && (
              <Chip
                label={task.priority}
                size="small"
                sx={{ height: 18, fontSize: '0.7rem' }}
                color={
                  task.priority === 'urgent' ? 'error' :
                  task.priority === 'high' ? 'warning' :
                  'default'
                }
              />
            )}
          </Box>
        </Box>
      </Box>
    </ListItem>
  );
};

// Task Group Component
const TaskGroup = ({ title, tasks, defaultExpanded = true, expanded, onToggle }) => {
  return (
    <Box sx={{ mb: 2 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1,
          cursor: 'pointer'
        }}
        onClick={onToggle}
      >
        <Typography variant="subtitle2" color="textSecondary">
          {title} ({tasks.length})
        </Typography>
        <IconButton size="small">
          {expanded ? <CollapseIcon /> : <ExpandIcon />}
        </IconButton>
      </Box>

      <Collapse in={expanded}>
        <List sx={{ p: 0 }}>
          {tasks.map(task => (
            <DraggableTask key={task._id} task={task} />
          ))}
        </List>
      </Collapse>
    </Box>
  );
};

// Main Sidebar Component
const TaskSidebar = ({ projectId, onTaskDragStart }) => {
  const theme = useTheme();
  const { gridTasks, isLoading, fetchProjectTasks } = useTaskStore();

  // Get tasks for this specific project from grid-specific storage
  const tasks = gridTasks[projectId] || [];

  console.log('TaskSidebar - tasks for project:', projectId, tasks);
  console.log('TaskSidebar - isLoading:', isLoading);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredTasks, setFilteredTasks] = useState([]);

  // Persist expanded state across re-renders using useRef
  const expandedStateRef = React.useRef({
    unassigned: true,
    todo: false,
    inProgress: false,
    done: false
  });

  const [expandedState, setExpandedState] = useState(expandedStateRef.current);

  // Update ref when state changes
  React.useEffect(() => {
    expandedStateRef.current = expandedState;
  }, [expandedState]);

  const toggleGroup = (groupKey) => {
    setExpandedState(prev => ({
      ...prev,
      [groupKey]: !prev[groupKey]
    }));
  };

  useEffect(() => {
    if (projectId) {
      // For grid view, use the existing task fetching but request more results
      fetchProjectTasks(projectId, { 
        limit: 200, // Request more tasks
        sort: '-updatedAt,-createdAt'
      });
    }
  }, [projectId, fetchProjectTasks]);

  useEffect(() => {
    // Filter and group tasks
    const filtered = tasks.filter(task => 
      task.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      task.description?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    setFilteredTasks(filtered);
  }, [tasks, searchQuery]);

  // Group tasks by status
  const taskGroups = {
    todo: filteredTasks.filter(t => t.status === 'to_do'),
    inProgress: filteredTasks.filter(t => t.status === 'in_progress'),
    done: filteredTasks.filter(t => t.status === 'done')
  };

  const unassignedTasks = filteredTasks.filter(
    task => !task.gridAssignments || task.gridAssignments.length === 0
  );

  return (
    <Paper
      sx={{
        width: 320,
        alignSelf: 'stretch',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        // borderRadius: theme.aghrba.tokens.borderRadius.card,
        borderRadius: 1,
        ml: 2
      }}
    >
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h6" gutterBottom>
          Project Tasks
        </Typography>
        
        {/* Search */}
        <TextField
          size="small"
          fullWidth
          placeholder="Search tasks..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            )
          }}
        />
      </Box>

      {/* Task List */}
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 2 }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : filteredTasks.length === 0 ? (
          <Typography variant="body2" color="textSecondary" align="center">
            No tasks found
          </Typography>
        ) : (
          <>
            {/* Unassigned tasks first */}
            {unassignedTasks.length > 0 && (
              <TaskGroup
                title="Unassigned to Grid"
                tasks={unassignedTasks}
                expanded={expandedState.unassigned}
                onToggle={() => toggleGroup('unassigned')}
              />
            )}

            {/* Tasks by status */}
            {taskGroups.todo.length > 0 && (
              <TaskGroup
                title="To Do"
                tasks={taskGroups.todo}
                expanded={expandedState.todo}
                onToggle={() => toggleGroup('todo')}
              />
            )}

            {taskGroups.inProgress.length > 0 && (
              <TaskGroup
                title="In Progress"
                tasks={taskGroups.inProgress}
                expanded={expandedState.inProgress}
                onToggle={() => toggleGroup('inProgress')}
              />
            )}

            {taskGroups.done.length > 0 && (
              <TaskGroup
                title="Completed"
                tasks={taskGroups.done}
                expanded={expandedState.done}
                onToggle={() => toggleGroup('done')}
              />
            )}
          </>
        )}
      </Box>

      {/* Footer Hint */}
      <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <Typography variant="caption" color="textSecondary">
          Drag tasks to grid cells to assign them
        </Typography>
      </Box>
    </Paper>
  );
};

export default TaskSidebar;